import { GameWorld, eGameObject } from "./GameWorld";
import { RND } from "../../Utils/RND";
import { TIMER } from "../Gameplay";
import { Rectangle } from "../../Utils/Shapes/Rectangle";
import { Character, eDeathReason } from "../Player/Character";
import { eSfxSprite, Game, SfxSprites, eAudioAsset, rnd } from "../../Game";
import { SoundManager } from "../../Utils/SoundManager";



export const enum eBarrierState {
    close,
    opening,
    open,
    closing
}



export class BarrierSettings {
    get minCloseTime(): number { return this._minCloseTime; }
    private _minCloseTime: number;

    get maxCloseTime(): number { return this._maxCloseTime; }
    private _maxCloseTime: number;

    get minOpenTime(): number { return this._minOpenTime; }
    private _minOpenTime: number;

    get maxOpenTime(): number { return this._maxOpenTime; }
    private _maxOpenTime: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(minCloseTime: number, maxCloseTime: number, minOpenTime: number, maxOpenTime: number) {
        this._minCloseTime = minCloseTime;
        this._maxCloseTime = maxCloseTime;
        this._minOpenTime = minOpenTime;
        this._maxOpenTime = maxOpenTime;
    }
}



export class Barrier {
    private static readonly OPEN_ANGLE = 60;

    private static readonly BARRIER_PILLAR_OFFSET = 0.09;
    private static readonly BARRIER_COLRC_W = 1;
    private static readonly BARRIER_COLRC_H = 0.05;

    private static STATE_UPDATE_FNC: (() => void)[];

    private _entPillar: pc.Entity;
    private _entBarrier: pc.Entity;

    /**
     * (RO) Kolizni obdelnik
     */
    get colRect(): Rectangle { return this._colRect; }
    private _colRect: Rectangle;

    /**
     * (RO) Aktualni stav brany
     */
    get state(): eBarrierState { return this._state; }
    private _state: eBarrierState;

    /**
     * (RO) Predchozi hodnota prubehu stavu otevirani/zavirani brany (0 az 1)
     */
    get statePreProgress(): number { return this._statePreProgress; }
    private _statePreProgress: number;

    /**
     * (RO) Aktualni hodnota prubehu stavu otevirani/zavirani brany (0 az 1)
     */
    get stateCurProgress(): number { return this._stateCurProgress; }
    private _stateCurProgress: number;

    private _stateTime: number;
    private _stateLen: number;

    private _settings: BarrierSettings;
    private _leftSided: boolean;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        if (!Barrier.STATE_UPDATE_FNC) {
            Barrier.STATE_UPDATE_FNC = [];
            Barrier.STATE_UPDATE_FNC[eBarrierState.close] = this.processClose;
            Barrier.STATE_UPDATE_FNC[eBarrierState.opening] = this.processOpening;
            Barrier.STATE_UPDATE_FNC[eBarrierState.open] = this.processOpen;
            Barrier.STATE_UPDATE_FNC[eBarrierState.closing] = this.processClosing;
        }

        // Kolizni obdelnik
        this._colRect = new Rectangle();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(parent: pc.Entity, pos: pc.Vec3, leftSided: boolean, behindPillar: boolean, settings: BarrierSettings): void {
        if (!this._entPillar)
            this._entPillar = GameWorld.instance.getObjectNewEntity(eGameObject.post);

        this._entPillar.setLocalPosition(pos);

        if (this._entPillar.parent != parent)
            this._entPillar.reparent(parent);

        // Zavora
        if (!this._entBarrier)
            this._entBarrier = GameWorld.instance.getObjectNewEntity(eGameObject.barrier);

        this._entBarrier.setLocalPosition(pos.x, pos.y + 0.21, pos.z + Barrier.BARRIER_PILLAR_OFFSET * (behindPillar ? -1 : 1));

        if (this._entBarrier.parent != parent)
            this._entBarrier.reparent(parent);

        if (!this._entBarrier.sound) {
            SoundManager.createSoundComponent(this._entBarrier, {
                positional: true,
                distanceModel: pc.DISTANCE_LINEAR,
                maxDistance: 2
            });

            SoundManager.addSoundSlot(this._entBarrier, eSfxSprite.barrierDown, SfxSprites[eSfxSprite.barrierDown], Game.APP.assets.find(eAudioAsset.sfx, pc.eAssetType.audio).id);
        }

        this._leftSided = leftSided;
        this._settings = settings;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    deactivate(): void {
        this._entPillar = undefined;
        this._entBarrier = undefined;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._colRect.setTo(GameWorld.PATH_X, this._entBarrier.getPosition().z - Barrier.BARRIER_COLRC_H / 2, Barrier.BARRIER_COLRC_W, Barrier.BARRIER_COLRC_H);

        this._state = rnd.randomBool() ? eBarrierState.open : eBarrierState.close;
        this._stateTime = TIMER.time;
        this.setStateLen();

        let angles = this._entBarrier.getEulerAngles();
        angles.z = (this._state == eBarrierState.open ? Barrier.OPEN_ANGLE : 0);

        if (!this._leftSided)
            angles.z = 180 - angles.z;

        this._entBarrier.setEulerAngles(angles);

        this._entBarrier.sound.stop(eSfxSprite.barrierDown);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        Barrier.STATE_UPDATE_FNC[this._state].call(this);
    }

    //-------------------------------------------------------------------------
    /**
     * Overeni kolize hrace se zavorou
     * @param plChar
     */
    //-------------------------------------------------------------------------
    checkPlCollision(plChar: Character): void {
        if (this._state == eBarrierState.open)
            return;

        let plRc = plChar.collisionRect;

        if (this._colRect.intersect(plRc)) {
            if (this._state == eBarrierState.close) {
                this.flipPlDir(plChar);
            }
            else if (this._state == eBarrierState.opening) {
                if (this._stateCurProgress < 0.25)
                    this.flipPlDir(plChar);
            }
            else if (this._state == eBarrierState.closing) {
                if (this._stateCurProgress > 0.8) {
                    if (this._statePreProgress < 0.8) {
                        if (!plChar.hasShield)
                            plChar.kill(eDeathReason.collision);
                    }
                    else {
                        this.flipPlDir(plChar);
                    }
                }
            }
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Zmeni smer chuze hrace, pokud se jedna o postavu v cele formace. Jinak postavu zabije.
     * @param plChar 
     */
    //-------------------------------------------------------------------------
    private flipPlDir(plChar: Character): void {
        let dir = plChar.dir;

        if ((dir > 0 && plChar.formationBotOffset == 0) || (dir < 0 && plChar.formationTopOffset == 0)) {
            // Zmenit smer pohybu hrace
            let plShift: number;

            if (dir > 0) {
                plShift = (this._colRect.top - plChar.collisionRect.bottom) - 0.01;
            }
            else {
                plShift = (this._colRect.bottom - plChar.collisionRect.top) + 0.01;
            }

            plChar.player.flipWalkDir(plShift, false);
        }
        else {
            // Zabit postavu
            if (!plChar.hasShield)
                plChar.kill(eDeathReason.collision);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processClose(): void {
        if (TIMER.time >= this._stateTime + this._stateLen) {
            // Zahajit otevreni brany
            this._state = eBarrierState.opening;
            this._stateTime = TIMER.time;
            this._statePreProgress = this._stateCurProgress = 0;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processOpening(): void {
        let progress = (TIMER.time - this._stateTime) / 2000;

        if (progress >= 1) {
            progress = 1;

            this._state = eBarrierState.open;
            this._stateTime = TIMER.time;
            this.setStateLen();
        }

        this._statePreProgress = this._stateCurProgress;
        this._stateCurProgress = progress;

        let angles = this._entBarrier.getEulerAngles();
        angles.z = Barrier.OPEN_ANGLE * progress;

        if (!this._leftSided)
            angles.z = 180 - angles.z;

        this._entBarrier.setEulerAngles(angles);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processOpen(): void {
        if (TIMER.time >= this._stateTime + this._stateLen) {
            // Zahajit otevreni brany
            this._state = eBarrierState.closing;
            this._stateTime = TIMER.time;
            this._statePreProgress = this._stateCurProgress = 0;

            SoundManager.playSound(this._entBarrier, eSfxSprite.barrierDown);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processClosing(): void {
        let progress = (TIMER.time - this._stateTime) / 1000;

        if (progress >= 1) {
            progress = 1;

            this._state = eBarrierState.close;
            this._stateTime = TIMER.time;
            this.setStateLen();
        }

        progress = pc.BounceOut(progress);

        this._statePreProgress = this._stateCurProgress;
        this._stateCurProgress = progress;

        let angles = this._entBarrier.getEulerAngles();
        angles.z = Barrier.OPEN_ANGLE - Barrier.OPEN_ANGLE * progress;

        if (!this._leftSided)
            angles.z = 180 - angles.z;

        this._entBarrier.setEulerAngles(angles);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private setStateLen(): void {
        if (this._state == eBarrierState.open) {
            this._stateLen = rnd.randomIntIn(this._settings.minOpenTime, this._settings.maxOpenTime);
        }
        else {
            this._stateLen = rnd.randomIntIn(this._settings.minCloseTime, this._settings.maxCloseTime);
        }
    }
}